import { api } from './client';
import type { TwoFactorMethod, UpdateProfilePayload } from '@/types';
import type { components } from './generated/schema';

/** Тело `POST /auth/add-password` — из контракта сервера (`AddPasswordDto`). */
type AddPasswordBody = components['schemas']['AddPasswordDto'];

// WEB-133 §F10 — body for POST /auth/2fa/verify (exactly one of code / recoveryCode).
export interface Verify2faPayload {
  challengeToken: string;
  code?: string;
  recoveryCode?: string;
}

// WEB-133 §F10 — body for DELETE /auth/2fa (one of code / recoveryCode / password).
export interface Disable2faPayload {
  code?: string;
  recoveryCode?: string;
  password?: string;
}

// WEB-1091 — identity confirmation for POST /auth/2fa/setup: `password` for
// accounts that have one, `googleAccessToken` (fresh reauth) for Google-only
// accounts. See backend/src/auth/services/two-factor.service.ts
// (assertIdentityConfirmed).
export interface Setup2faIdentity {
  password?: string;
  googleAccessToken?: string;
}

/**
 * WEB-1247 — назначение письма, которое кабинет вправе попросить у
 * POST /auth/2fa/send-code. Зеркало серверного `TwoFactorSendPurpose`
 * (backend/src/auth/services/two-factor.service.ts): перечень закрыт одним
 * значением, «вход» и «подключение» сервер выбирает сам.
 */
export type TwoFactorSendPurpose = 'disable';

/**
 * WEB-133 §F10 — body for POST /auth/2fa/recovery-codes (one of code /
 * recoveryCode).
 *
 * Ревизия пачки 37 (С5, решение координатора Б8): в ветке ОДНОРАЗОВОГО кода
 * сервер требует ещё и пароль — перевыпуск обесценивает все прежние
 * резервные коды, то есть по последствиям равен снятию защиты (DELETE
 * /auth/2fa), где пароль спрашивали с самого начала. В ветке РЕЗЕРВНОГО кода
 * пароль не нужен: резервный код сам по себе — доказательство личности.
 * Аккаунт без пароля (вход через Google) сервер по паролю не проверяет.
 */
export interface RegenerateRecoveryCodesPayload {
  code?: string;
  recoveryCode?: string;
  password?: string;
}

export const authApi = {
  login: (email: string, password: string) =>
    api.post('/auth/login', { email, password }),
  // WEB-1330 п.1: termsAccepted обязателен на сервере — факт и момент
  // согласия с условиями пишутся в профиль (users.terms_accepted_at).
  // Значение идёт от галочки формы, не зашито: сервер отвергает false.
  register: (
    email: string,
    password: string,
    username: string | undefined,
    termsAccepted: boolean,
  ) =>
    api.post('/auth/register', { email, password, username, termsAccepted }),
  logout: () => api.post('/auth/logout'),
  googleAuth: (accessToken: string) => api.post('/auth/google', { accessToken }),
  me: () => api.get('/auth/me'),
  // P1 §F8 — persist cabinet settings (theme/language/timezone/timezoneAuto).
  // Returns the full updated profile (RichUserResponseDto).
  updateProfile: (payload: UpdateProfilePayload) => api.patch('/user', payload),
  // WEB-643 DangerZone — удаление собственного аккаунта. BE: DELETE /user
  // { password } (JWT); требует подтверждения текущим паролем (bcrypt.compare).
  deleteAccount: (password: string) => api.delete('/user', { data: { password } }),
  requestPasswordReset: (email: string) =>
    api.post('/auth/request-password-reset', { email }),
  // WEB-229 — step 2 of password-reset: exchange 6-digit code for a one-time token.
  // BE: POST /auth/verify-reset-password-code { email, code } → { token, message }
  verifyResetPasswordCode: (email: string, code: string) =>
    api.post('/auth/verify-reset-password-code', { email, code }),
  resetPassword: (token: string, newPassword: string) =>
    api.post('/auth/reset-password', { token, newPassword }),
  // WEB-443 B2 — change password for a user who already has one (classic flow:
  // verified by the current password). BE: POST /auth/change-password (JWT).
  changePassword: (currentPassword: string, newPassword: string) =>
    api.post('/auth/change-password', { currentPassword, newPassword }),
  // WEB-3758 доводка 1 (решение владельца 24.10 п. 32) — пароль входящему
  // через Google: подтверждение свежим токеном Google из окна пароля, не кодом
  // из письма. BE: POST /auth/add-password { password, googleAccessToken } (JWT;
  // assertGoogleReauth, WEB-1091). 401 здесь — отказ Google, а не конец сессии
  // (путь в refreshExemptPaths).
  addPassword: (password: string, googleAccessToken: string) => {
    const body: AddPasswordBody = { password, googleAccessToken };
    return api.post('/auth/add-password', body);
  },
  sendVerificationCode: (email: string) =>
    api.post('/auth/send-verification-code', { email }),
  verifyEmail: (email: string, code: string) =>
    api.post('/auth/verify-email', { email, code }),
  resendVerificationCode: (email: string) =>
    api.post('/auth/resend-verification-code', { email }),

  // ── WEB-133 §F10 — two-factor authentication ──────────────────────────────
  // Login-challenge flow (public — uses challengeToken, not a session JWT).
  verify2fa: (payload: Verify2faPayload) =>
    api.post('/auth/2fa/verify', payload),
  sendChallengeCode: (challengeToken: string) =>
    api.post('/auth/2fa/send-code-challenge', { challengeToken }),
  // Enrollment / management flow (requires an active session / Bearer token).
  // WEB-1091: identity confirmation is now required — see Setup2faIdentity.
  setup2fa: (method: TwoFactorMethod, identity: Setup2faIdentity) =>
    api.post('/auth/2fa/setup', { method, ...identity }),
  verifySetup2fa: (code: string) =>
    api.post('/auth/2fa/verify-setup', { code }),
  // WEB-1247: у ручки появилось тело с назначением письма. Попросить можно
  // только «отключение» (код под операцию снятия защиты / перевыпуска
  // резервных кодов) — «вход» и «подключение» сервер выбирает сам по
  // состоянию аккаунта. Без довода поведение прежнее, байт-в-байт.
  sendCode2fa: (purpose?: TwoFactorSendPurpose) =>
    api.post('/auth/2fa/send-code', purpose ? { purpose } : {}),
  disable2fa: (payload: Disable2faPayload) =>
    api.delete('/auth/2fa', { data: payload }),
  regenerateRecoveryCodes: (payload: RegenerateRecoveryCodesPayload) =>
    api.post('/auth/2fa/recovery-codes', payload),
  // WEB-329 — persist the active organization for the current user.
  // PATCH /user/active-org { orgId } → { success, message }
  setActiveOrg: (orgId: string) => api.patch('/user/active-org', { orgId }),
  // WEB-340 — persist the active project for the current user (WEB-204 §F8).
  // PATCH /user/active-project { projectId } → { success, message }
  // Returns 403 if caller is not a member of the target project.
  setActiveProject: (projectId: string) => api.patch('/user/active-project', { projectId }),
  // WEB-499 - Variant A: persist "no active project" (org-mode) so a reload
  // restores org-mode instead of falling back to the last project. No body,
  // no membership check - always allowed for the caller's own row.
  // DELETE /user/active-project -> { success, message }
  clearActiveProject: () => api.delete('/user/active-project'),
};
